import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Award, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, MapPin, Clock, ShieldAlert, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADE_CONFIG = {
  Excellent: { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', ring: 'ring-emerald-500/40', icon: <CheckCircle2 className="w-4 h-4" /> },
  Good: { color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/30', ring: 'ring-sky-500/40', icon: <TrendingUp className="w-4 h-4" /> },
  'Needs Improvement': { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', ring: 'ring-amber-500/40', icon: <TrendingDown className="w-4 h-4" /> },
  'At Risk': { color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', ring: 'ring-red-500/40', icon: <AlertTriangle className="w-4 h-4" /> },
};

function ScoreRing({ score, grade, size = 64 }) {
  const cfg = GRADE_CONFIG[grade] || GRADE_CONFIG['Good'];
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const strokeColor = grade === 'Excellent' ? '#34d399' : grade === 'Good' ? '#38bdf8' : grade === 'Needs Improvement' ? '#fbbf24' : '#f87171';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={strokeColor} strokeWidth="4"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-sm font-bold ${cfg.color}`}>{score}</span>
      </div>
    </div>
  );
}

function ScorecardRow({ card, rank }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = GRADE_CONFIG[card.grade] || GRADE_CONFIG['Good'];

  return (
    <Card className={`glass-card border ${cfg.bg} transition-all duration-200`} data-testid={`scorecard-${rank}`}>
      <CardContent className="p-0">
        <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-center gap-4 text-left" data-testid={`scorecard-toggle-${rank}`}>
          <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">{rank}</span>
          <ScoreRing score={card.score} grade={card.grade} size={48} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">{card.caregiver_name}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color}`}>
                {cfg.icon}{card.grade}
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">{card.email} &middot; {card.total_visits} visits</p>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs flex-shrink-0">
            <Metric icon={<Clock className="w-3 h-3" />} label="Late" value={card.late_count} danger={card.late_count > 5} />
            <Metric icon={<MapPin className="w-3 h-3" />} label="Geofence" value={card.geofence_breaches} danger={card.geofence_breaches > 2} />
            <Metric icon={<ShieldAlert className="w-3 h-3" />} label="Missed" value={card.missed_visits} danger={card.missed_visits > 0} />
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
        </button>

        {expanded && (
          <div className="px-4 pb-4 pt-0 border-t border-border/50">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <DetailCard label="Late Arrivals" value={card.late_count} sub={`avg ${card.avg_late_minutes} min`} color="text-amber-400" />
              <DetailCard label="Geofence Breaches" value={card.geofence_breaches} sub="outside radius" color="text-red-400" />
              <DetailCard label="Missed Visits" value={card.missed_visits} sub="no check-in" color="text-red-400" />
              <DetailCard label="Total Visits" value={card.total_visits} sub="check-ins" color="text-[#00A6A0]" />
            </div>
            <div className="mt-3 p-2 bg-muted/50 rounded-lg">
              <p className="text-[10px] text-muted-foreground">
                <strong>Score Breakdown:</strong> Starts at 100. Late arrivals: -{Math.min(card.late_count * 2, 40)} (max -40).
                {card.avg_late_minutes > 10 ? ` Avg lateness: -${Math.min(Math.round((card.avg_late_minutes - 10) * 0.5), 15)} (max -15).` : ''}
                {card.geofence_breaches > 0 ? ` Geofence: -${Math.min(card.geofence_breaches * 5, 25)} (max -25).` : ''}
                {card.missed_visits > 0 ? ` Missed: -${Math.min(card.missed_visits * 8, 40)} (max -40).` : ''}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ icon, label, value, danger }) {
  return (
    <div className={`flex items-center gap-1 ${danger ? 'text-red-400' : 'text-muted-foreground'}`}>
      {icon}<span className="font-medium">{value}</span><span className="text-muted-foreground">{label}</span>
    </div>
  );
}

function DetailCard({ label, value, sub, color }) {
  return (
    <div className="bg-background/50 rounded-lg p-3 text-center">
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-foreground font-medium">{label}</p>
      <p className="text-[10px] text-muted-foreground">{sub}</p>
    </div>
  );
}

export default function CaregiverScorecard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchScorecard(); }, []);

  const fetchScorecard = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/api/visit-audit/scorecard`, { withCredentials: true });
      setData(res);
    } catch (err) {
      toast.error('Failed to load scorecards');
    }
    setLoading(false);
  };

  const filtered = data?.scorecards?.filter(c => filter === 'all' || c.grade === filter) || [];
  const gradeCounts = {};
  (data?.scorecards || []).forEach(c => { gradeCounts[c.grade] = (gradeCounts[c.grade] || 0) + 1; });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="caregiver-scorecard-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Award className="w-6 h-6 text-[#00A6A0]" />
            Caregiver Reliability Scorecard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Performance scores based on punctuality, location compliance, and visit attendance
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12"><p className="text-muted-foreground">Loading scorecards...</p></div>
        ) : !data || data.scorecards.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">No caregivers found</p>
            <p className="text-xs text-muted-foreground mt-1">Scores will appear once caregivers have visit data</p>
          </div>
        ) : (
          <>
            {/* Org summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card className="glass-card border-[#00A6A0]/30" data-testid="org-avg-score">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-[#00A6A0]">{data.org_avg_score}</p>
                  <p className="text-xs text-muted-foreground">Org Average Score</p>
                </CardContent>
              </Card>
              {Object.entries(GRADE_CONFIG).map(([grade, cfg]) => (
                <Card key={grade} className={`glass-card border cursor-pointer transition-all ${filter === grade ? cfg.bg + ' ring-2 ' + cfg.ring : 'border-border hover:border-border/80'}`}
                  onClick={() => setFilter(f => f === grade ? 'all' : grade)} data-testid={`filter-${grade.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardContent className="p-4 text-center">
                    <div className={`flex items-center justify-center gap-1 mb-1 ${cfg.color}`}>
                      {cfg.icon}
                      <p className="text-2xl font-bold">{gradeCounts[grade] || 0}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{grade}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filter !== 'all' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Filtering by: <strong className="text-foreground">{filter}</strong></span>
                <button onClick={() => setFilter('all')} className="text-xs text-[#00A6A0] hover:underline" data-testid="clear-filter">Clear</button>
              </div>
            )}

            {/* Scorecard list */}
            <div className="space-y-2" data-testid="scorecard-list">
              {filtered.map((card, idx) => (
                <ScorecardRow key={card.caregiver_id} card={card} rank={idx + 1} />
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-8"><p className="text-muted-foreground text-sm">No caregivers match this filter</p></div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
